import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';

/** What the viewer may do on a matter beyond what their `role` allows. The enterprise matter page
 * sets these for an affiliate admin whose firm reaches the client; every other app mounts no
 * provider and reads the all-false default, so nothing renders differently for them. */
export interface IServiceRequestViewerCapabilities {
  /** Pause and unpause, cancel, end service, reset to step. */
  canManageRun: boolean;
  canDeleteServiceRequest: boolean;
  canCreateInformationRequest: boolean;
  canRequestSignature: boolean;
  /** Where the page takes the viewer once the matter is cancelled or deleted. Only read when
   * `canManageRun` or `canDeleteServiceRequest`. */
  onServiceRequestClosed?: () => void;
}

const SERVICE_REQUEST_VIEWER_CAPABILITIES_NONE: IServiceRequestViewerCapabilities =
  {
    canManageRun: false,
    canDeleteServiceRequest: false,
    canCreateInformationRequest: false,
    canRequestSignature: false,
  };

const ServiceRequestViewerCapabilitiesContext =
  createContext<IServiceRequestViewerCapabilities>(
    SERVICE_REQUEST_VIEWER_CAPABILITIES_NONE
  );

interface ServiceRequestViewerCapabilitiesProviderProps {
  capabilities: IServiceRequestViewerCapabilities;
  children: ReactNode;
}

export const ServiceRequestViewerCapabilitiesProvider = ({
  capabilities,
  children,
}: ServiceRequestViewerCapabilitiesProviderProps) => (
  <ServiceRequestViewerCapabilitiesContext.Provider value={capabilities}>
    {children}
  </ServiceRequestViewerCapabilitiesContext.Provider>
);

/** Never throws outside a provider: the default is "no extra capability". */
export const useServiceRequestViewerCapabilities =
  (): IServiceRequestViewerCapabilities =>
    useContext(ServiceRequestViewerCapabilitiesContext);
